import React from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { Button } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { Download } from 'lucide-react';

import { ExportDescription } from './ExportDescription';
import { ExportSummaryControlBar } from './ExportSummaryControlBar';
import { ExportTabStrip } from './ExportTabStrip';
import { ExportTable } from './ExportTable';
import { FinalExportDialog } from './FinalExportDialog';
import { SequenceControlsBar } from '../../antibody-analysis/components/SequenceControlsBar';
import { ChainWidthProvider } from '../../antibody-analysis/context/ChainWidthContext';
import { exportToCSV } from '../../antibody-analysis/utilities/exportUtils';
import { useDeleteExport, useGetExperiment, useUpdateExport } from '../../store/store';
import { useExportSummaryData } from '../hooks/useExportSummaryData';
import { useSendToExportSummary } from '../hooks/useSendToExportSummary';
import { type ExportRow, type ExportTabId, MAX_EXPORT_TABS, SELECTED_TAB_ID } from '../types';
import { formatExportFileTimestamp } from '../utilities/exportSummaryUtils';

// Layout mirrors AntibodyAnalysisPage's collapsed-grid branch — control bars stacked above a
// table region — so Export summary and Hit selection read as the same page shape.
const s = {
  page: 'flex flex-col h-full min-w-0 bg-background',
  body: 'flex-1 overflow-hidden flex flex-col',
  tabSummaryRow: 'min-h-9',
  counts: 'my-0 px-4 py-2 text-sm text-[#6b7772]',
  error: 'px-4 py-10 text-sm text-[#c62828]',
  loading: 'px-4 py-10 text-sm text-[#6b7772]',
  tableWrap: 'flex-1 min-h-0 overflow-hidden',
  footer: 'flex justify-end px-4 py-3',
  downloadIcon: 'w-4 h-4',
} as const;

/**
 * Export summary (SSTSW-1045/1046/1047).
 *
 * The table is the flattened Hit selection table: one row per node, carrying the
 * clonotype-level columns as well, rendered with the same cell components so sequence
 * tinting, SHM markers and liability badges match. Per-node editing and the editable status
 * chips are deliberately absent — deferred scope.
 */
export function ExportSummaryPage() {
  const { id: experimentId } = useParams<{ id: string }>();
  const { exports: fetchedExports, clonotypes, clonotypeDefinition, rowsByNodeId, isLoading, error } = useExportSummaryData(experimentId);
  const { data: experiment } = useGetExperiment({ id: experimentId! });

  const exports = fetchedExports;
  const { sendToExportSummary } = useSendToExportSummary(experimentId);
  const [updateExport] = useUpdateExport();
  const [deleteExport] = useDeleteExport();
  // Hit selection's Export button hands over the id it just created, so we open that tab rather
  // than the default Selected one.
  const location = useLocation();
  const newExportId = (location.state as { newExportId?: string } | null)?.newExportId;
  const [activeTabId, setActiveTabId] = React.useState<ExportTabId>(newExportId ?? SELECTED_TAB_ID);

  const [selectedNodeIds, setSelectedNodeIds] = React.useState<ReadonlySet<number>>(() => new Set());
  const seededNodeIds = React.useRef<Set<number>>(new Set());
  React.useEffect(() => {
    const unseeded = exports.flatMap((exportTab) => exportTab.nodes.map((node) => node.nodeId)).filter((id) => !seededNodeIds.current.has(id));
    if (unseeded.length === 0) {
      return;
    }
    unseeded.forEach((id) => seededNodeIds.current.add(id));
    setSelectedNodeIds((previous) => new Set([...previous, ...unseeded]));
  }, [exports]);

  const isSelectedTab = activeTabId === SELECTED_TAB_ID;
  const activeExport = exports.find((exportTab) => exportTab.exportId === activeTabId);

  const allNodeIds = React.useMemo(() => [...new Set(exports.flatMap((e) => e.nodes.map((n) => n.nodeId)))], [exports]);

  const rows: ExportRow[] = React.useMemo(() => {
    const nodeIds = isSelectedTab ? allNodeIds.filter((id) => selectedNodeIds.has(id)) : (activeExport?.nodes.map((n) => n.nodeId) ?? []);
    return nodeIds.map((id) => rowsByNodeId.get(id)).filter((row): row is ExportRow => row !== undefined);
  }, [isSelectedTab, allNodeIds, activeExport, rowsByNodeId, selectedNodeIds]);

  const selectedInView = React.useMemo(() => rows.filter((row) => selectedNodeIds.has(row.nodeId)).length, [rows, selectedNodeIds]);
  const uniqueSelectedNodeIds = React.useMemo(() => allNodeIds.filter((id) => selectedNodeIds.has(id)), [allNodeIds, selectedNodeIds]);
  const uniqueSelectedCount = uniqueSelectedNodeIds.length;
  const selectionSignature = React.useMemo(() => [...uniqueSelectedNodeIds].sort((a, b) => a - b).join(','), [uniqueSelectedNodeIds]);
  const totalSelectedCount = React.useMemo(
    () => exports.reduce((total, e) => total + e.nodes.filter((n) => selectedNodeIds.has(n.nodeId)).length, 0),
    [exports, selectedNodeIds],
  );

  const handleToggle = React.useCallback((nodeId: number) => {
    setSelectedNodeIds((previous) => {
      const next = new Set(previous);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const handleToggleAll = React.useCallback(() => {
    const visible = rows.map((row) => row.nodeId);
    setSelectedNodeIds((previous) => {
      const next = new Set(previous);
      const allOn = visible.every((id) => next.has(id));
      visible.forEach((id) => (allOn ? next.delete(id) : next.add(id)));
      return next;
    });
  }, [rows]);

  const isSelected = React.useCallback((nodeId: number) => selectedNodeIds.has(nodeId), [selectedNodeIds]);

  const [exportDialogOpen, setExportDialogOpen] = React.useState(false);

  // On the Selected tab the dialog confirms the unique Selected set across every export.
  const selectedRows: ExportRow[] = React.useMemo(
    () => uniqueSelectedNodeIds.map((id) => rowsByNodeId.get(id)).filter((row): row is ExportRow => row !== undefined),
    [uniqueSelectedNodeIds, rowsByNodeId],
  );

  const rowsToExport = isSelectedTab ? selectedRows : rows.filter((row) => selectedNodeIds.has(row.nodeId));

  const handleExport = React.useCallback(() => {
    // Reuses the existing 43-column CSV writer, the same one Hit selection uses, so the two
    // downloads are identical in shape.
    const selectedSequenceIds = new Set(rowsToExport.map((row) => row.sequenceData.metadata.sequenceId));
    const selectedData = clonotypes
      .map((clonotype) => ({
        ...clonotype,
        sequences: clonotype.sequences.filter((sequence) => selectedSequenceIds.has(sequence.metadata.sequenceId)),
      }))
      .filter((clonotype) => clonotype.sequences.length > 0);

    const clonotypeSizeMap = new Map(clonotypes.map((c) => [c.id, c.sequences.reduce((sum, sequence) => sum + sequence.metadata.barcodes.length, 0)]));
    const timestamp = formatExportFileTimestamp();
    const slug = (experiment?.title ?? 'experiment')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    /*
     * "<date> <time>_[export-N_]<experiment>.csv" — date first, per SSTSW-1172's example
     * The export number only appears when downloading a single Export tab.
     */
    const exportNumber = isSelectedTab ? '' : `export-${activeExport?.exportNumber ?? ''}_`;

    exportToCSV(selectedData, `${timestamp}_${exportNumber}${slug}.csv`, clonotypeSizeMap, experimentId, clonotypeDefinition);
    setExportDialogOpen(false);
  }, [rowsToExport, clonotypes, experiment, experimentId, clonotypeDefinition, isSelectedTab, activeExport]);

  const handleDescriptionChange = React.useCallback(
    (description: string) => {
      if (experimentId && activeExport) {
        updateExport({ exportUuid: activeExport.exportId, exportUpdate: { description } })
          .unwrap()
          .catch(() => showNotification({ title: 'Could not save description', message: 'Your change was not saved. Please try again.', color: 'red' }));
      }
    },
    [experimentId, activeExport, updateExport],
  );

  const handleAddExport = React.useCallback(() => {
    if (!experimentId || exports.length >= MAX_EXPORT_TABS) {
      return;
    }
    // A new tab from whatever is currently checked
    sendToExportSummary(selectedRows.map((row) => row.nodeId));
  }, [experimentId, exports.length, sendToExportSummary, selectedRows]);

  const handleCloseExport = React.useCallback(
    (exportId: string) => {
      if (!experimentId) {
        return;
      }
      if (activeTabId === exportId) {
        setActiveTabId(SELECTED_TAB_ID);
      }
      deleteExport({ exportUuid: exportId })
        .unwrap()
        .catch(() => showNotification({ title: 'Could not delete export', message: 'The export was not deleted. Please try again.', color: 'red' }));
    },
    [experimentId, activeTabId, deleteExport],
  );

  return (
    <ChainWidthProvider clonotypes={clonotypes}>
      <div className={s.page}>
        <ExportTabStrip
          exports={exports}
          activeTabId={activeTabId}
          selectedCount={uniqueSelectedCount}
          selectionSignature={selectionSignature}
          isLoading={isLoading}
          onTabChange={setActiveTabId}
          onCloseExport={handleCloseExport}
          onAddExport={handleAddExport}
        />

        <div className={s.tabSummaryRow}>
          {isSelectedTab ? (
            <p className={s.counts}>
              {totalSelectedCount} selected across {exports.length} exports · {uniqueSelectedCount} unique sequences
            </p>
          ) : (
            activeExport && <ExportDescription description={activeExport.description} onChange={handleDescriptionChange} />
          )}
        </div>

        <div className={s.body}>
          <ExportSummaryControlBar rowCount={rows.length} selectedCount={selectedInView} />
          <SequenceControlsBar />

          <div className={s.tableWrap}>
            {isLoading && <p className={s.loading}>Loading sequences…</p>}
            {!isLoading && error && <p className={s.error}>Could not load exports. Please refresh the page, or try again in a moment.</p>}
            {!isLoading && !error && <ExportTable rows={rows} isSelected={isSelected} onToggle={handleToggle} onToggleAll={handleToggleAll} />}
          </div>

          <div className={s.footer}>
            <Button
              radius="xl"
              color="#1263c4"
              disabled={rowsToExport.length === 0}
              leftSection={isSelectedTab ? undefined : <Download className={s.downloadIcon} />}
              onClick={() => setExportDialogOpen(true)}
            >
              {isSelectedTab ? 'Finalize export' : 'Download export'}
            </Button>
          </div>
        </div>
      </div>

      <FinalExportDialog opened={exportDialogOpen} onClose={() => setExportDialogOpen(false)} rows={rowsToExport} onExport={handleExport} />
    </ChainWidthProvider>
  );
}
